import partition from 'lodash/partition';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';

import {
  BAD_THRESHOLD,
  GOOD_THRESHOLD,
  GREAT_THRESHOLD,
  TERRIBLE_THRESHOLD,
} from '../constants';
import { AttributeVariant, FitVariant } from './types';

export function getAttributeVariantFromDifferential(value: number) {
  if (value > GREAT_THRESHOLD) {
    return 'movedUp';
  } else if (value > GOOD_THRESHOLD) {
    return 'up';
  } else if (value < TERRIBLE_THRESHOLD) {
    return 'movedDown';
  } else if (value < BAD_THRESHOLD) {
    return 'down';
  }

  return null;
}

export function getColorClassNamesFromAttributeVariant(
  variant?: AttributeVariant,
) {
  if (variant === 'up') {
    return 'text-green-900 bg-green-100';
  } else if (variant === 'movedUp') {
    return 'text-white bg-green';
  } else if (variant === 'down') {
    return 'text-red-900 bg-red-100';
  } else if (variant === 'movedDown') {
    return 'text-white bg-red';
  } else {
    return 'text-gray-900 bg-gray-100';
  }
}

export function getFitVariantFromFitScore(value?: number): FitVariant {
  if (!value) {
    return null;
  }

  if (value > GREAT_THRESHOLD) {
    return 'great';
  } else if (value > GOOD_THRESHOLD) {
    return 'good';
  } else if (value < BAD_THRESHOLD) {
    return 'poor';
  } else {
    return 'average';
  }
}

type FitVariantColorClassNamesOptions = {
  muted?: boolean;
};
export function getColorClassNamesFromFitVariant(
  variant?: FitVariant,
  options?: FitVariantColorClassNamesOptions,
) {
  if (variant === 'great') {
    return 'text-white bg-green-500 font-semibold';
  } else if (variant === 'good') {
    return 'text-white bg-blue-500 font-semibold';
  } else if (variant === 'poor' && !options?.muted) {
    return 'text-white bg-red-500 font-semibold';
  } else if (variant === 'poor' && options?.muted) {
    return 'text-white bg-gray-400 font-semibold';
  } else {
    return 'text-gray-600 bg-gray-100';
  }
}

export function getDescriptionFromFitVariant(variant?: FitVariant) {
  if (variant === 'great') {
    return 'Great Fit';
  } else if (variant === 'good') {
    return 'Good Fit';
  } else if (variant === 'poor') {
    return 'Bad Fit';
  } else if (variant === 'average') {
    return 'Average Fit';
  } else {
    return 'Processing...';
  }
}

export function getShortDescriptionFromFitVariant(variant?: FitVariant) {
  if (variant === 'great') {
    return 'Great';
  } else if (variant === 'good') {
    return 'Good';
  } else if (variant === 'poor') {
    return 'Bad';
  } else if (variant === 'average') {
    return 'Avg';
  } else {
    return '...';
  }
}

type PartitionOptions = {
  sort?: boolean;
};
export function partitionAttributesByDifferential(
  attributes: number,
  options?: PartitionOptions,
) {
  const [negative, positive] = partition(
    attributes,
    (value) => value.differential < BAD_THRESHOLD,
  );

  if (options?.sort) {
    return [
      sortBy(negative, (value) => value.differential),
      sortBy(positive, (value) => value.differential).reverse(),
    ];
  }

  return [negative, positive];
}

export function calculateLikelihoodPercentageFromDifferential(
  differential: number,
) {
  if (differential < BAD_THRESHOLD) {
    return toPercentage(1 - differential);
  }

  return toPercentage(differential - 1);
}

function toPercentage(value: number) {
  return `${round(value * 100, 2)}%`;
}
